import { Ref, ref } from 'vue';
import StickyAlert, { alertIsOfType } from '@/modules/alerts/models/sticky-alert';
import { StickyAlertType } from '@/modules/alerts/models/sticky-alert-type';
import StickyAlertAnswerMap from '@/modules/alerts/models/sticky-alert-answer-map';

const alerts: Ref<StickyAlert<StickyAlertType>[]> = ref([]);

const MAXIMUM_ALERTS = 3;

const findAlert = <T extends StickyAlertType>(
  stickyAlertType: T,
  id: string,
): StickyAlert<T> | null => {
  const targetIndex = alerts.value.findIndex(a => a.props.id === id);
  if (targetIndex < 0) {
    console.warn(`Failed to find alert with ID "${id}".`);
    return null;
  }
  const alert = alerts.value[targetIndex];
  if (!alertIsOfType(stickyAlertType, alert)) {
    console.error(`Alert with ID "${id}" does not match specified type "${stickyAlertType}".`);
    return null;
  }
  return alert;
};

const answerAlert = <T extends StickyAlertType>(
  alert: StickyAlert<T>,
  answer: StickyAlertAnswerMap[T],
) => {
  const targetIndex = alerts.value.findIndex(a => a.props.id === alert.props.id);
  if (targetIndex < 0) {
    console.error(`Failed to remove alert with ID "${alert.props.id}".`);
    return;
  }
  alert.provideAnswer(answer);
  alerts.value.splice(targetIndex, 1);
};

const addAlert = (alert: StickyAlert<StickyAlertType>) => {
  alerts.value.unshift(alert);
  if (alerts.value.length > MAXIMUM_ALERTS) {
    const alertToRemove = alerts.value[alerts.value.length - 1];
    answerAlert(alertToRemove, { result: 'TIME_OUT' });
  }
};

const clearAlerts = () => {
  alerts.value.splice(0, alerts.value.length);
};

const setupTimeout = <T extends StickyAlertType>(
  alertType: T,
  alertId: string,
  alert: StickyAlert<T>,
  timeoutMs?: number,
) => {
  if (!timeoutMs) {
    console.debug('timeoutMs is 0 (or falsy), not setting timeout.');
    return;
  }

  console.debug(`Creating dismiss timeout for ${timeoutMs}ms from now.`);
  setTimeout(() => {
    const delayedAlert = findAlert(alertType, alertId);
    if (delayedAlert === null) {
      console.debug('Failed to find alert while timing out, it must have already been closed.');
      return;
    }
    answerAlert(alert, { result: 'TIME_OUT' } as StickyAlertAnswerMap[T]);
  }, timeoutMs);
};

export default () => {
  return { alerts, addAlert, findAlert, answerAlert, clearAlerts, setupTimeout };
};
